import { useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  IconButton,
  Chip,
  TablePagination,
  Box,
} from "@mui/material";
import { Visibility as EyeIcon, Edit as EditIcon } from "@mui/icons-material";
import { useOpenLead } from "../../hooks/useOpenLead";
import { DEAL_HEADERS, RIDER_CLIENT_ID, RIDER_DEAL_HEADERS } from "../../utils/constants";
import { getPriorityColor, getUserInfo } from "../../utils/helpers";

export const DealsTable = ({ deals = [], stages, onViewDeal, pagination }) => {
  const openLead = useOpenLead();
  const { userId, userName } = getUserInfo();
  const isTargetClient = userId === "69afdc065323c68fd76cdabf";
  const isRiderClient = userId === RIDER_CLIENT_ID;

  const filteredHeaders = DEAL_HEADERS.filter(header => {
    const isComplaintHeader = [
      "LOGGED BY", "CALLER NAME", "PAYMENT TYPE", "MACHINE #",
      "REASON FOR CONTACT", "COMPLAINT TYPE", "ERROR CODE",
      "JUICES PUSHED COUNT", "CREDIT NOTE JUICES COUNT", "COMMAND CODE",
      "STATUS", "REFUNDS (AED)", "COMMENTS"
    ].includes(header);
    return isTargetClient || !isComplaintHeader;
  });

  const [localPage, setLocalPage] = useState(0);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(10);

  // With `pagination` the server pages the data and `deals` is already the
  // current page; without it the table pages `deals` itself.
  const page = pagination ? pagination.page : localPage;
  const rowsPerPage = pagination ? pagination.rowsPerPage : localRowsPerPage;
  const totalCount = pagination ? pagination.total : deals.length;

  const handleChangePage = (_, newPage) => {
    if (pagination) pagination.onPageChange(newPage);
    else setLocalPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const rows = parseInt(event.target.value, 10);
    if (pagination) {
      pagination.onRowsPerPageChange(rows);
    } else {
      setLocalRowsPerPage(rows);
      setLocalPage(0);
    }
  };

  const paginatedDeals = pagination
    ? deals
    : deals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // The rider client tracks riders, not customers, so its list view uses an
  // entirely different column set rather than the standard lead columns.
  if (isRiderClient) {
    const riderColumnWidths = {
      "RIDER ID": 130,
      "RIDER NAME": 180,
      "MOBILE NO": 150,
      "PLATFORM": 140,
      "BIKE NO": 120,
      "VISA NO": 140,
      "CURRENT STORE": 170,
      "PREVIOUS STORE": 170,
      "DESCRIPTION": 250,
      "STATUS": 120,
      "PRIORITY": 100,
      "STAGE": 130,
      "DATE": 120,
      "ACTIONS": 100,
    };

    const textCell = (value, maxWidth) => (
      <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth }}>
        <Typography variant="body2" sx={{ color: "#475569" }} title={value || "—"}>
          {value || "—"}
        </Typography>
      </TableCell>
    );

    return (
      <Paper sx={{ boxShadow: "none", border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden" }}>
        <TableContainer sx={{
          overflow: "auto",
          maxWidth: '100%',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}>
          <Table sx={{ minWidth: 1800, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                {RIDER_DEAL_HEADERS.map((header) => (
                  <TableCell key={header} sx={{ whiteSpace: "nowrap", width: riderColumnWidths[header] || 150 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: "#475569", fontSize: "0.7rem", letterSpacing: "0.03em" }}>
                      {header}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedDeals.map((deal) => {
                const priorityColors = getPriorityColor(deal.priority);
                return (
                  <TableRow key={deal._id || deal.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: "#0f172a" }}>
                        {deal.riderId || "—"}
                      </Typography>
                    </TableCell>
                    {textCell(deal.riderName, 180)}
                    {textCell(deal.mobile, 150)}
                    {textCell(deal.riderPlatform, 140)}
                    {textCell(deal.bikeNo, 120)}
                    {textCell(deal.visaNo, 140)}
                    {textCell(deal.currentStore, 170)}
                    {textCell(deal.previousStore, 170)}
                    {textCell(deal.description, 250)}

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {deal.riderStatus ? (
                        <Chip
                          label={deal.riderStatus}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: "0.65rem", borderRadius: "6px" }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: "#475569" }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={deal.priority?.toUpperCase() || "MEDIUM"}
                        size="small"
                        sx={{
                          bgcolor: priorityColors.light,
                          color: priorityColors.text,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          borderRadius: "6px"
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={deal.stageName || "New"}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: "0.65rem", borderRadius: "6px" }}
                      />
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
                        {deal.createdAt
                          ? new Date(deal.createdAt).toLocaleDateString("en-AE", { day: '2-digit', month: 'short', year: 'numeric' })
                          : "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => openLead(deal._id || deal.id)}
                          sx={{ color: "#6366f1", "&:hover": { bgcolor: "#f5f3ff" } }}
                          title="Edit Lead"
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}

              {paginatedDeals.length === 0 && (
                <TableRow>
                  <TableCell colSpan={RIDER_DEAL_HEADERS.length} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No riders found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: "1px solid #f1f5f9" }}
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ boxShadow: "none", border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden" }}>
      <TableContainer sx={{
        overflow: "auto",
        maxWidth: '100%',
        // Hide scrollbar
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none', // IE and Edge
        scrollbarWidth: 'none', // Firefox
      }}>
        <Table sx={{ minWidth: isTargetClient ? 2000 : 900, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#F9FAFB" }}>
              {filteredHeaders.map((header) => {
                let width = 150;
                if (header === "DEAL NAME") width = 200;
                if (header === "COMPANY NAME") width = 180;
                if (header === "PHONE NO") width = 150;
                if (header === "EMAIL") width = 200;
                if (header === "STAGE") width = 120;
                if (header === "PRIORITY") width = 100;
                if (header === "DATE") width = 120;
                if (header === "LOGGED BY") width = 150;
                if (header === "CALLER NAME") width = 150;
                if (header === "PAYMENT TYPE") width = 150;
                if (header === "MACHINE #") width = 120;
                if (header === "REASON FOR CONTACT") width = 200;
                if (header === "COMPLAINT TYPE") width = 150;
                if (header === "ERROR CODE") width = 120;
                if (header === "JUICES PUSHED COUNT") width = 160;
                if (header === "CREDIT NOTE JUICES COUNT") width = 180;
                if (header === "COMMAND CODE") width = 120;
                if (header === "STATUS") width = 150;
                if (header === "REFUNDS (AED)") width = 130;
                if (header === "COMMENTS") width = 250;
                if (header === "ACTIONS") width = 100;

                return (
                  <TableCell key={header} sx={{ whiteSpace: "nowrap", width: width }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: "#475569", fontSize: "0.7rem", letterSpacing: "0.03em" }}>
                      {header}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedDeals.map((deal) => {
              const priorityColors = getPriorityColor(deal.priority);
              return (
                <TableRow key={deal.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: "#0f172a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                      title={deal.dealName}
                    >
                      {deal.dealName}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180 }}>
                    <Typography variant="body2" sx={{ color: "#475569" }} title={deal.companyName || deal.company?.name || "—"}>
                      {deal.companyName || deal.company?.name || "—"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" sx={{ color: "#475569" }}>{deal.mobile}</Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" sx={{ color: "#475569" }}>{deal.email || deal.customerEmailAddress || "—"}</Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Chip
                      label={deal.stageName || "New"}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: "0.65rem", borderRadius: "6px" }}
                    />
                    {deal.closingDate && (
                      <Typography variant="caption" sx={{ display: "block", color: "#94a3b8", fontSize: "0.65rem", mt: 0.5 }}>
                        {new Date(deal.closingDate).toLocaleDateString("en-AE", { day: "2-digit", month: "short", year: "numeric" })}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Chip
                      label={deal.priority?.toUpperCase() || "MEDIUM"}
                      size="small"
                      sx={{
                        bgcolor: priorityColors.light,
                        color: priorityColors.text,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        borderRadius: "6px"
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
                      {new Date(deal.createdAt).toLocaleDateString("en-AE", { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Typography>
                  </TableCell>

                  {isTargetClient && (
                    <>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.loggedBy || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.callerName || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.paymentType || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.machineNumber || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.reasonForContact || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.complaintType || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.errorCode || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.juicesPushed || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.creditNoteJuices || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.commandCode || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.complaintStatus || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>{deal.refundAmount || "—"}</Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "#64748b",
                            fontSize: "0.8rem"
                          }}
                          title={deal.complaintComments}
                        >
                          {deal.complaintComments || "—"}
                        </Typography>
                      </TableCell>
                    </>
                  )}

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => openLead(deal._id || deal.id)}
                        sx={{ color: "#6366f1", "&:hover": { bgcolor: "#f5f3ff" } }}
                        title="Edit Lead"
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}

            {paginatedDeals.length === 0 && (
              <TableRow>
                <TableCell colSpan={filteredHeaders.length} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No deals found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{ borderTop: "1px solid #f1f5f9" }}
      />
    </Paper>
  );
};
